import { ChangeDetectionStrategy, Component } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { validateDateFn } from '@app/core/entity/validation';
import {
  areDaysBetweenDatesOk,
  areMonthsBetweenFinancialYearsOk,
  isFirstDateAfterSecondDate,
} from '@app/shared/misc/dates';
import { filter, map, withLatestFrom } from 'rxjs';
import { Client } from 'src/app/core/entity/client';
import { CloudDbData } from 'src/app/core/entity/cloud-db-data';

import { CommonModule } from '@angular/common';
import { ExportWizardActions } from '@app/core/state/export-wizard/export-wizard.actions';
import { exportWizardFeature } from '@app/core/state/export-wizard/export-wizard.feature';
import { toClientTypeTransformer } from '@app/core/state/transformers/transformers';
import { TranslocoModule } from '@jsverse/transloco';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { FocusTrapModule } from 'primeng/focustrap';
import { RadioButtonModule } from 'primeng/radiobutton';
import { isNetherlands } from 'src/app/core/entity/locale';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableFormDialogBaseComponent } from 'src/app/core/i18n/translatable-form-dialog-base.component';
import { FirmSelectors } from 'src/app/core/state/firm/firm.selectors';
import { BlFrontendButtonComponent } from 'src/app/core/wrappers/bl-frontend-button.component';
import { CalendarComponent } from '../calendar/calendar.component';
import { DialogData, DialogResult } from '../form-dialog-base/form-dialog-base.component';

export interface WizardConfig extends DialogData {
  client: Client;
}
export interface WizardResponse extends DialogResult {
  clientId: number;
  name?: string;
  financialYear: {
    start: string;
    end: string;
  };
  accountingMethod: string;
  vatAccountingInterval: string;
}

interface WizardFinancialYearGroup {
  start: FormControl<string>;
  end: FormControl<string>;
}

interface WizardForm {
  clientId: FormControl<number>;
  name: FormControl<string>;
  financialYear: FormGroup<WizardFinancialYearGroup>;
  accountingMethod: FormControl<string>;
  vatAccountingInterval: FormControl<string>;
}

const TRANSLATION_SCOPE = 'exportWizardDialogComponent';

@Component({
  selector: 'app-export-wizard-dialog',
  templateUrl: './export-wizard-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  providers: [getTranslationProvider(TRANSLATION_SCOPE)],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RadioButtonModule,
    FocusTrapModule,
    CalendarComponent,
    TranslocoModule,
    BlFrontendButtonComponent,
  ],
})
export class ExportWizardDialogComponent extends TranslatableFormDialogBaseComponent<
  WizardConfig,
  WizardResponse,
  WizardForm
> {
  exportCompanyName: string;
  accountingMethods: { name: string; key: string }[] = [
    { name: 'billingMethod', key: 'billing' },
    { name: 'cashMethod', key: 'cash' },
  ];

  vatAccountingIntervals: { name: string; key: string }[] = [
    { name: 'month', key: 'month' },
    { name: 'quarter', key: 'quarter' },
    { name: 'year', key: 'year' },
    { name: 'noneOrTaxExemptBusiness', key: 'none' },
  ];

  onSubmit() {
    if (this.form.invalid) {
      return;
    }

    this.close(this.getFormValue());
  }

  get isFormValid() {
    return this.form.valid;
  }

  get submitTitle() {
    if (!this.form?.errors) {
      return '';
    }

    const message: string[] = Object.values(this.form.errors).reduce((acc, curr) => {
      if (!curr) {
        return acc;
      }

      return acc.concat(curr.message);
    }, []);

    return message ? message.join('\n') : '';
  }

  protected static override getTranslationScope(): string {
    return TRANSLATION_SCOPE;
  }

  protected static override getDialogConfig(
    translation: Record<string, string>,
  ): Omit<DynamicDialogConfig<WizardConfig>, 'data'> {
    return {
      width: '550px',
      resizable: false,
      draggable: false,
      modal: true,
      header: translation.extraInfoHeadline,
      style: { maxHeight: '90vh' },
      styleClass: 'export-wizard-dialog tabbed-dialog',
    };
  }

  protected override onInitialized = () => {
    if (!this.config.data.client) {
      return;
    }

    this.createForm();
    this.store
      .select(exportWizardFeature.selectData)
      .pipe(
        filter((data) => Boolean(data)),
        withLatestFrom(this.store.select(FirmSelectors.selectCountryCode)),
        map(([data, countryCode]) => {
          if (isNetherlands(countryCode) && !data.accountingMethod) {
            return { ...data, accountingMethod: 'billing' };
          }
          return data;
        }),
        this.takeUntilDestroyed(),
      )
      .subscribe((data) => this.setDefaultFormValues(data));

    this.store.dispatch(
      ExportWizardActions.getDefaultValues({ client: toClientTypeTransformer.transform(this.config.data.client) }),
    );
  };

  private getFormValue(): WizardResponse {
    return this.form.value as WizardResponse;
  }

  private setDefaultFormValues(defaultFormValues: CloudDbData) {
    this.form.patchValue({ ...defaultFormValues, name: defaultFormValues.name ?? '' });
  }

  private createForm() {
    this.form = this.builder.group<WizardForm>(
      {
        clientId: new FormControl(this.config.data.client.id),
        name: new FormControl(null),
        financialYear: this.builder.group({
          start: new FormControl('', validateDateFn({ key: 'start' })),
          end: new FormControl('', validateDateFn({ key: 'end' })),
        }),
        accountingMethod: new FormControl(null, Validators.required),
        vatAccountingInterval: new FormControl(null, Validators.required),
      },
      { validators: this.validateRequiredFields() },
    );
  }

  private getAccountingMethodValue(form: FormGroup<WizardForm>) {
    return form.controls.accountingMethod.value ? null : { message: this.translate('accountingMethodNotSelected') };
  }

  private getVatAccountingIntervalValue(form: FormGroup<WizardForm>) {
    return form.controls.vatAccountingInterval.value ? null : { message: this.translate('vatReportingNotSelected') };
  }

  private getFromValue(start: string) {
    return start ? null : { message: this.translate('financialYearFromDateNotSelected') };
  }

  private getToValue(end: string) {
    return end ? null : { message: this.translate('financialYearToDateNotSelected') };
  }

  private getIsEndAfterStartValue(start: string, end: string) {
    return isFirstDateAfterSecondDate(end, start) ? null : { message: this.translate('toDateMustBeAfterFromDate') };
  }

  private getAreDaysBetweenOkValue(start: string, end: string) {
    return areDaysBetweenDatesOk(start, end)
      ? null
      : { message: this.translate('thereMustBeAtLeast2DaysInFinancialYear') };
  }

  private getIsMonthsBetweenOk(start: string, end: string) {
    return areMonthsBetweenFinancialYearsOk(start, end)
      ? null
      : { message: this.translate('financialYearMayNotBeLongerThan18Months') };
  }

  private validateRequiredFields(): ValidatorFn {
    return (form: FormGroup<WizardForm>): ValidationErrors | null => {
      const accountingMethod = this.getAccountingMethodValue(form);
      const vatAccountingInterval = this.getVatAccountingIntervalValue(form);
      const start = form.controls.financialYear.controls.start.value;
      const end = form.controls.financialYear.controls.end.value;
      const from = this.getFromValue(start);
      const to = this.getToValue(end);
      const isEndAfterStart = this.getIsEndAfterStartValue(start, end);
      const isDaysBetweenOk = this.getAreDaysBetweenOkValue(start, end);
      const isMonthsBetweenOk = this.getIsMonthsBetweenOk(start, end);

      const errors = {
        accountingMethod,
        vatAccountingInterval,
        start: from,
        end: to,
        isEndAfterStart,
        isDaysBetweenOk,
        isMonthsBetweenOk,
      };

      if (Object.values(errors).some((error) => error)) {
        return errors;
      }

      return null;
    };
  }
}
